import {
    BadgeCheck, CheckCircle2, ChevronRight, CreditCard, Download, FileText, Headphones,
    MapPin, PackageCheck, ReceiptText, RotateCcw, ShieldCheck, Star, Truck, UserRound, XCircle,
} from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store";
import {
    fetchOrderById,
    clearCurrentOrder,
    cancelOrder,
    requestReturnOrder,
    requestReplacementOrder,
    fetchOrderActions,
} from "../../../Redux/custmoer_orders/slice";
import { OrderItem, OrderStatus, SalarOrder } from "../OrdersPage/OrdersTypes";
import { transformOrderToSalarOrder } from "../OrdersPage/dataTransformers";
import { Order } from "../../../Redux/custmoer_orders/types";
import "./OrderDetailsPage.css";
import HomeNavbar from "../../globalcomponents/HomeNavbar/HomeNavbar";
import HomeSecondaryNavbar from "../../globalcomponents/HomeSecondaryNavbar/HomeSecondaryNavbar";
import MobileDetailNavbar from "../../globalcomponents/mobile/MobileDetailNavbar/MobileDetailNavbar";
import { useCart } from "../../../context/CartContext";
import ChatPanel from "../../globalcomponents/ChatPanel/ChatPanel";
import axios from "axios";
import { getCookie } from "../../../utility/getCookie";
import { startCashfreeCheckout } from "../../../utility/cashfreeCheckout";

type ActionType = "CANCEL" | "RETURN" | "REPLACE";

const formatPrice = (value: number) =>
    new Intl.NumberFormat("en-IN").format(value);

const PLACEHOLDER_IMAGE = "/assets/images/logos/salarlogo.png";

const getStatusLabel = (status: OrderStatus) => {
    if (status === "out-for-delivery") return "Out for delivery";
    if (status === "shipped") return "Shipped";
    if (status === "delivered") return "Delivered";
    if (status === "returned") return "Return completed";
    if (status === "cancelled") return "Cancelled";
    return "Order confirmed";
};

const paymentStatusBadgeClass = (status: string): string => {
    if (status === "Success" || status === "COMPLETED") return "payment-success";
    if (status === "Failed" || status === "FAILED") return "payment-failed";
    if (status === "Refunded" || status === "REFUNDED") return "payment-refunded";
    return "payment-pending";
};

const refundStatusClass = (status: string): string => {
    if (status === "REFUNDED" || status === "COMPLETED") return "refund-completed";
    return "refund-pending";
};

const isPaymentPending = (status: string): boolean =>
    status !== "Success" &&
    status !== "Refunded" &&
    status !== "COMPLETED" &&
    status !== "REFUNDED";

const getProductRoute = (order: SalarOrder, item: OrderItem): string => {
    const isReferral =
        order.purchaseType === "MLM" || item.sourceType === "REFERRAL";
    return isReferral
        ? `/Refproductdetails/${item.productId}`
        : `/DetailPage/${item.productId}`;
};

const OrderDetailsPage = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch<AppDispatch>();
    const { cartCount } = useCart();
    const { orderId } = useParams<{ orderId: string }>();
    const [browsingHistoryOpen, setBrowsingHistoryOpen] = useState(false);

    // Redux state
    const { currentOrder, loading, error, actionsMap } = useSelector(
        (state: RootState) => state.customerOrders,
    );

    // Modal State
    const [showModal, setShowModal] = useState(false);
    const [actionType, setActionType] = useState<ActionType | null>(null);
    const [reason, setReason] = useState("");
    const [details, setDetails] = useState("");
    const [actionLoading, setActionLoading] = useState(false);

    // Chat Support State
    const [chatOpen, setChatOpen] = useState(false);

    // Payment retry state
    const [retryLoadingId, setRetryLoadingId] = useState<string | null>(null);

    // Track if we've already fetched actions (avoid infinite loops)
    const fetchedActionsRef = useRef<Set<string>>(new Set());

    // Fetch order by ID on mount
    useEffect(() => {
        if (orderId) {
            dispatch(fetchOrderById(orderId));
        }
        return () => {
            dispatch(clearCurrentOrder());
        };
    }, [orderId, dispatch]);

    // Fetch order actions (guarded)
    useEffect(() => {
        if (orderId && !fetchedActionsRef.current.has(orderId)) {
            fetchedActionsRef.current.add(orderId);
            dispatch(fetchOrderActions(orderId));
        }
    }, [orderId, dispatch]);

    // Transform API order to UI order
    const order = useMemo<SalarOrder | null>(() => {
        if (!currentOrder) return null;
        return transformOrderToSalarOrder(currentOrder as Order);
    }, [currentOrder]);

    // Merge action eligibility
    const actions = order ? actionsMap[order.id] : undefined;
    const canCancel = actions?.canCancel;
    const canReturn = actions?.canReturn;
    const canReplace = actions?.canReplace;

    // Derive price summary from API order
    const priceSummary = useMemo(() => {
        if (!currentOrder) return null;
        const couponDiscount = currentOrder.couponDetails?.couponDiscount || 0;
        return {
            itemSubtotal: currentOrder.payableAmount || currentOrder.finalAmount?.amount || 0,
            shippingFee: currentOrder.finalAmount?.deliveryCharges || 0,
            platformFee: currentOrder.finalAmount?.platformFee || 0,
            productDiscount: (currentOrder.offersApplied?.totalDiscount || 0) + couponDiscount,
            cashOnDeliveryFee: 0,
            total: currentOrder.finalAmount?.finalPrice || currentOrder.payableAmount || 0,
            grandTotal: currentOrder.finalAmount?.finalPrice || currentOrder.payableAmount || 0,
        };
    }, [currentOrder]);

    // Derive payment info from API order
    const paymentInfo = useMemo(() => {
        if (!currentOrder) return null;
        return {
            method: currentOrder.paymentDetails?.paymentInstrument || "Online",
            status: currentOrder.paymentStatus || "Pending",
            reference: currentOrder.paymentDetails?.gatewayTransactionId || "",
            invoiceAvailable:
                currentOrder.paymentStatus === "Success" ||
                currentOrder.paymentStatus === "COMPLETED",
            invoiceNumber: currentOrder.orderNo || currentOrder.orderNumber || "",
        };
    }, [currentOrder]);

    // Derive address from API order (prefer structured deliveryAddress, fall back to flat fields)
    const address = useMemo(() => {
        if (!currentOrder) return null;
        const addr = currentOrder.deliveryAddress;
        return {
            name: addr?.fullName || currentOrder.deliveryFullName || "",
            mobile: addr?.mobile || currentOrder.deliveryMobile || "",
            line1: addr?.addressLine1 || currentOrder.deliveryAddressLine1 || "",
            line2: addr?.addressLine2 || currentOrder.deliveryAddressLine2 || "",
            landmark: addr?.landmark || currentOrder.deliveryLandmark || "",
            city: addr?.city || currentOrder.deliveryCity || "",
            state: addr?.state || currentOrder.deliveryState || "",
            pincode: addr?.pincode || currentOrder.deliveryPincode || "",
            country: addr?.country || currentOrder.deliveryCountry || "India",
        };
    }, [currentOrder]);

    const hasAddress = address && (address.line1 || address.city);

    const handleRetryPayment = async (paymentId: string) => {
        setRetryLoadingId(paymentId);
        try {
            const token = getCookie("access_token");
            const baseUrl = [
                process.env.REACT_APP_API_BASE_URL,
                process.env.REACT_APP_PAYMENT_APP_BACKEND_PREFIX,
            ].filter(Boolean).join("");

            const response = await axios.post(
                `${baseUrl}/payments/${paymentId}/retry`,
                {},
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "application/json",
                    },
                }
            );

            if (response.data?.success && response.data?.data?.paymentSessionId) {
                await startCashfreeCheckout(
                    response.data.data.paymentSessionId,
                    response.data.data.paymentMode,
                );
            } else if (response.data?.success && response.data?.data?.redirectUrl) {
                window.location.href = response.data.data.redirectUrl;
            } else if (response.data?.success && response.data?.data?.status === "COMPLETED") {
                alert("Payment completed successfully!");
                if (orderId) dispatch(fetchOrderById(orderId));
            } else {
                alert("Failed to initiate payment retry");
            }
        } catch (err: any) {
            alert(err.response?.data?.message || "An error occurred while retrying payment");
        } finally {
            setRetryLoadingId(null);
        }
    };

    const openProduct = (item: OrderItem) => {
        if (!order) return;
        navigate(getProductRoute(order, item));
    };

    const openBuyAgain = (sourceId: string | number) => {
        navigate("/buy-again", { state: { sourceProductId: sourceId } });
    };

    const openActionModal = (type: ActionType) => {
        setActionType(type);
        setReason("");
        setDetails("");
        setShowModal(true);
    };

    const handleActionSubmit = async () => {
        if (!order || !actionType) return;
        if (!reason.trim()) {
            alert("Please provide a reason");
            return;
        }

        setActionLoading(true);
        try {
            if (actionType === "CANCEL") {
                await dispatch(cancelOrder({ orderId: order.id, reason })).unwrap();
            } else if (actionType === "RETURN") {
                await dispatch(requestReturnOrder({ orderId: order.id, reason, details })).unwrap();
            } else if (actionType === "REPLACE") {
                await dispatch(requestReplacementOrder({ orderId: order.id, reason, details })).unwrap();
            }
            setShowModal(false);
            if (orderId) dispatch(fetchOrderById(orderId));
        } catch (err: any) {
            alert(err || `Failed to ${actionType.toLowerCase()} order`);
        } finally {
            setActionLoading(false);
        }
    };

    const handleImageError = (e: React.SyntheticEvent<HTMLImageElement>) => {
        e.currentTarget.src = PLACEHOLDER_IMAGE;
    };

    // Loading state
    if (loading && !order) {
        return (
            <>
                <div className="order-details-desktop-navbar">
                    <HomeNavbar variant="home" />
                </div>
                <MobileDetailNavbar cartCount={cartCount} searchPlaceholder="Search Salar" fallbackPath="/Orders" />
                <div className="order-details-desktop-secondary-navbar">
                    <HomeSecondaryNavbar variant="detail" browsingHistoryOpen={browsingHistoryOpen} onBrowsingHistoryClick={() => setBrowsingHistoryOpen((c) => !c)} />
                </div>
                <main className="order-details-page">
                    <div className="order-details-shell">
                        <div className="order-details-loading">
                            <div className="order-details-spinner"></div>
                            <p>Loading order details...</p>
                        </div>
                    </div>
                </main>
            </>
        );
    }

    // Error state
    if (error && !order) {
        return (
            <>
                <div className="order-details-desktop-navbar">
                    <HomeNavbar variant="home" />
                </div>
                <MobileDetailNavbar cartCount={cartCount} searchPlaceholder="Search Salar" fallbackPath="/Orders" />
                <div className="order-details-desktop-secondary-navbar">
                    <HomeSecondaryNavbar variant="detail" browsingHistoryOpen={browsingHistoryOpen} onBrowsingHistoryClick={() => setBrowsingHistoryOpen((c) => !c)} />
                </div>
                <main className="order-details-page">
                    <div className="order-details-shell">
                        <div className="order-details-error">
                            <p>{error}</p>
                            <button type="button" onClick={() => orderId && dispatch(fetchOrderById(orderId))}>
                                Retry
                            </button>
                        </div>
                    </div>
                </main>
            </>
        );
    }

    // Not found
    if (!order) {
        return (
            <>
                <div className="order-details-desktop-navbar">
                    <HomeNavbar variant="home" />
                </div>
                <MobileDetailNavbar cartCount={cartCount} searchPlaceholder="Search Salar" fallbackPath="/Orders" />
                <div className="order-details-desktop-secondary-navbar">
                    <HomeSecondaryNavbar variant="detail" browsingHistoryOpen={browsingHistoryOpen} onBrowsingHistoryClick={() => setBrowsingHistoryOpen((c) => !c)} />
                </div>
                <main className="order-details-page">
                    <div className="order-details-shell">
                        <div className="order-details-error">
                            <p>Order not found</p>
                        </div>
                    </div>
                </main>
            </>
        );
    }

    const firstItem = order.items[0];

    return (
        <>
            <div className="order-details-desktop-navbar">
                <HomeNavbar variant="home" />
            </div>
            <MobileDetailNavbar cartCount={cartCount} searchPlaceholder="Search Salar" fallbackPath="/Orders" />
            <div className="order-details-desktop-secondary-navbar">
                <HomeSecondaryNavbar variant="detail" browsingHistoryOpen={browsingHistoryOpen} onBrowsingHistoryClick={() => setBrowsingHistoryOpen((current) => !current)} />
            </div>
            <main className="order-details-page">
                <div className="order-details-shell">
                    <div className="order-details-breadcrumb">
                        <button type="button" onClick={() => navigate("/HomePage")}>
                            Your Account
                        </button>
                        <ChevronRight size={15} />
                        <button type="button" onClick={() => navigate("/Orders")}>
                            Your Orders
                        </button>
                        <ChevronRight size={15} />
                        <strong>Order Details</strong>
                    </div>
                    <section className="order-details-hero">
                        <div>
                            <span> PURCHASE RECORD </span>
                            <h1>Order Details</h1>
                            <p> Placed on{" "}
                                <strong>{order.placedDate}</strong>{" "}· Order No{" "}
                                <strong>{order.orderNo} </strong>
                            </p>
                        </div>
                        <div className="order-details-hero-actions">
                            <span className={`order-details-status ${order.status}`}>
                                {getStatusLabel(order.status)}
                            </span>
                            {paymentInfo?.invoiceAvailable && (
                                <button type="button" onClick={() => navigate(`/Invoice/${order.id}`)}>
                                    <Download size={17} /> Download Invoice </button>
                            )}
                        </div>
                    </section>
                    <section className="order-overview-grid">
                        <article className="order-overview-card">
                            <div className="order-overview-title">
                                <MapPin size={21} />
                                <div>
                                    <span> DELIVERY </span>
                                    <h2> Ship To</h2>
                                </div>
                            </div>
                            <div className="order-address-block">
                                {hasAddress ? (
                                    <>
                                        {address!.name && <strong>{address!.name} </strong>}
                                        {address!.line1 && <span> {address!.line1} </span>}
                                        {address!.line2 && <span> {address!.line2} </span>}
                                        {address!.landmark && <span> Landmark: {address!.landmark} </span>}
                                        {(address!.city || address!.state) && (
                                            <span> {address!.city}{address!.city && address!.state ? ", " : ""}{address!.state} {address!.pincode} </span>
                                        )}
                                        <span> {address!.country} </span>
                                        {address!.mobile && (
                                            <div>
                                                <UserRound size={16} />
                                                <strong>{address!.mobile} </strong>
                                            </div>
                                        )}
                                    </>
                                ) : (
                                    <span className="order-address-placeholder"> Address not available </span>
                                )}
                            </div>
                        </article>
                        <article className="order-overview-card">
                            <div className="order-overview-title">
                                <CreditCard size={21} />
                                <div>
                                    <span> PAYMENT </span>
                                    <h2> Payment Method</h2>
                                </div>
                            </div>
                            <div className="order-payment-block">
                                <span> Paid using </span>
                                <strong> {paymentInfo?.method || "Online"} </strong>
                                {paymentInfo?.reference && <p> Gateway Ref: {paymentInfo.reference} </p>}
                                <div className={`order-payment-status-badge ${paymentStatusBadgeClass(order.paymentStatus)}`}>
                                    <CheckCircle2 size={17} />
                                    <span> {order.paymentStatus || "Pending"} </span>
                                </div>
                                {isPaymentPending(order.paymentStatus) && (
                                    <button
                                        type="button"
                                        className="order-complete-payment-btn"
                                        onClick={() => handleRetryPayment(order.id)}
                                        disabled={retryLoadingId === order.id}
                                    >
                                        {retryLoadingId === order.id ? "Processing..." : (<>
                                            <CreditCard size={16} /> Complete Payment
                                        </>)}
                                    </button>
                                )}
                            </div>
                        </article>
                        <article className="order-overview-card order-summary-card">
                            <div className="order-overview-title">
                                <ReceiptText size={21} />
                                <div>
                                    <span>BILLING </span>
                                    <h2> Order Summary </h2>
                                </div>
                            </div>
                            {priceSummary && (
                                <>
                                    <dl className="order-price-list">
                                        <div>
                                            <dt> Item(s) Subtotal </dt>
                                            <dd> ₹ {formatPrice(priceSummary.itemSubtotal)} </dd>
                                        </div>
                                        <div>
                                            <dt> Shipping </dt>
                                            <dd> {priceSummary.shippingFee === 0 ? "₹0" : `₹${formatPrice(priceSummary.shippingFee)}`} </dd>
                                        </div>
                                        <div>
                                            <dt> Platform Fee </dt>
                                            <dd> ₹{formatPrice(priceSummary.platformFee)} </dd>
                                        </div>
                                        <div>
                                            <dt> Product Discount</dt>
                                            <dd className="discount">
                                                -₹{formatPrice(priceSummary.productDiscount)}
                                            </dd>
                                        </div>
                                        <div>
                                            <dt> Cash/Pay on Delivery Fee</dt>
                                            <dd> ₹ {formatPrice(priceSummary.cashOnDeliveryFee)}</dd>
                                        </div>
                                        <div className="order-price-total-row">
                                            <dt> Total</dt>
                                            <dd>₹{formatPrice(priceSummary.total)}</dd>
                                        </div>
                                    </dl>
                                    <div className="order-grand-total">
                                        <span>Grand Total</span>
                                        <strong> ₹{formatPrice(priceSummary.grandTotal)}</strong>
                                    </div>
                                </>
                            )}
                            {order.salarCoinsEarned > 0 && (
                                <div className="order-coins-earned">
                                    <Star size={15} fill="currentColor" />
                                    <span> {order.salarCoinsEarned} Salar Coins earned </span>
                                </div>
                            )}
                        </article>
                    </section>

                    <section className="order-fulfilment-card">
                        <div className="order-fulfilment-head">
                            <div>
                                <span> ORDER FULFILMENT</span>
                                <h2>{order.statusTitle}</h2>
                                <p> {order.statusMessage}</p>
                            </div>
                            <div className="order-fulfilment-assurance">
                                <ShieldCheck size={20} />
                                <div>
                                    <span> SALAR ASSURED</span>
                                    <strong> Protected purchase</strong>
                                </div>
                            </div>
                        </div>
                        <div className="order-fulfilment-body">
                            <div className="order-detail-products">
                                {order.items.map((item) => (
                                    <article className="order-detail-product" key={item.id}>
                                        <button type="button" className="order-detail-product-image" onClick={() => openProduct(item)}>
                                            <img src={item.image} alt={item.title} onError={handleImageError} />
                                        </button>
                                        <div className="order-detail-product-copy">
                                            <button type="button" className="order-detail-product-link" onClick={() => openProduct(item)}>
                                                <strong>{item.title}</strong>
                                                {item.description && <span>{item.description}</span>}
                                            </button>
                                            <div className="order-detail-product-meta">
                                                <span> Qty {item.quantity}</span>
                                                {item.sourceType && <span> {item.sourceType} </span>}
                                            </div>
                                            <button type="button" className="order-detail-seller" onClick={() => navigate(`/seller/${item.sellerSlug}`)}>
                                                Sold by <strong>{item.sellerName}</strong>
                                                <BadgeCheck size={15} />
                                            </button>
                                            <div className="order-detail-product-price">
                                                <strong> ₹ {formatPrice(item.price)} </strong>
                                                {item.mrp && <del> ₹ {formatPrice(item.mrp)} </del>}
                                            </div>
                                            <div className="order-detail-inline-actions">
                                                {isPaymentPending(order.paymentStatus) ? (
                                                    <button
                                                        type="button"
                                                        className="complete-payment"
                                                        onClick={() => handleRetryPayment(order.id)}
                                                        disabled={retryLoadingId === order.id}
                                                    >
                                                        {retryLoadingId === order.id ? "Processing..." : (<>
                                                            <CreditCard size={17} /> Complete Payment
                                                        </>)}
                                                    </button>
                                                ) : (
                                                    <button type="button" className="buy-again"
                                                        onClick={() => openBuyAgain(item.sourceId)}>
                                                        <RotateCcw size={17} />
                                                        Buy Again
                                                    </button>
                                                )}
                                                <button type="button" onClick={() => openProduct(item)}>
                                                    View Product
                                                </button>
                                            </div>
                                        </div>
                                    </article>
                                ))}
                            </div>
                            <aside className="order-detail-action-panel">
                                {(order.status === "processing" || order.status === "shipped" || order.status === "out-for-delivery" || order.status === "delivered") && (
                                    <button type="button" className="primary" onClick={() => navigate(`/track-order/${order.id}`)}>
                                        <Truck size={18} />
                                        Track Package
                                    </button>
                                )}
                                {order.trackingUrl && (
                                    <a
                                        className="order-action-courier-link"
                                        href={order.trackingUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        <Truck size={18} />
                                        Track via Courier
                                    </a>
                                )}
                                {canCancel && (order.status === "processing" || order.status === "shipped" || order.status === "out-for-delivery") && (
                                    <button type="button" onClick={() => openActionModal("CANCEL")}>
                                        <XCircle size={18} />
                                        Request Cancellation
                                    </button>
                                )}
                                {(canReturn || canReplace) && (
                                    <button type="button" onClick={() => openActionModal("RETURN")}>
                                        <RotateCcw size={18} />
                                        Return or Replacement
                                    </button>
                                )}
                                {order.reviewEligible && firstItem && (
                                    <button type="button" onClick={() => navigate(`/SubmitReview/${firstItem.productId}`)}>
                                        <Star size={18} />
                                        Write Product Review
                                    </button>
                                )}
                                <button type="button" onClick={() => setChatOpen(true)}>
                                    <Headphones size={18} />
                                    Chat With Us
                                </button>
                            </aside>
                        </div>
                        {order.refundDetails.refundAmount > 0 && (
                            <div className="order-refund-footer">
                                <div>
                                    <span> Refund Amount </span>
                                    <strong> ₹ {formatPrice(order.refundDetails.refundAmount)} </strong>
                                </div>
                                <div>
                                    <span> Refund Status </span>
                                    <span className={`order-refund-badge ${refundStatusClass(order.refundDetails.refundStatus)}`}>
                                        {order.refundDetails.refundStatus}
                                    </span>
                                </div>
                                <div>
                                    <span> Delivery Status </span>
                                    <span className="order-refund-badge refund-info">
                                        {order.statusTitle}
                                    </span>
                                </div>
                            </div>
                        )}
                        <div className="order-fulfilment-footer">
                            <div>
                                <PackageCheck size={18} />
                                <span>
                                    Order ID{" "}
                                    <strong> {order.id} </strong>
                                </span>
                            </div>
                            <div>
                                <FileText size={18} />
                                <span>
                                    Invoice{" "}
                                    <strong> {paymentInfo?.invoiceNumber || "N/A"}</strong>
                                </span>
                            </div>
                        </div>
                    </section>
                </div >
            </main >

            {/* Action Modal Backdrop */}
            {showModal && (
                <div
                    className="order-modal-backdrop"
                    onClick={() => !actionLoading && setShowModal(false)}
                />
            )}

            {/* Action Modal */}
            {showModal && (
                <div className="order-modal">
                    <div className="order-modal-dialog">
                        <div className="order-modal-content">
                            <div className="order-modal-header">
                                <h5>
                                    {actionType === "CANCEL"
                                        ? "Cancel Order"
                                        : actionType === "RETURN"
                                            ? "Request Return"
                                            : "Request Replacement"}
                                </h5>
                                <button
                                    type="button"
                                    className="order-modal-close"
                                    onClick={() => setShowModal(false)}
                                    disabled={actionLoading}
                                    aria-label="Close"
                                >
                                    &times;
                                </button>
                            </div>
                            <div className="order-modal-body">
                                <p className="order-modal-subtitle">
                                    {actionType === "CANCEL"
                                        ? "Are you sure you want to cancel this order? This action cannot be undone."
                                        : `Please provide the reason and any additional details for your ${actionType?.toLowerCase()} request.`}
                                </p>
                                <div className="order-modal-field">
                                    <label>Reason</label>
                                    <select
                                        value={reason}
                                        onChange={(e) => setReason(e.target.value)}
                                        disabled={actionLoading}
                                    >
                                        <option value="">Select a reason</option>
                                        {actionType === "CANCEL" ? (
                                            <>
                                                <option value="Changed my mind">Changed my mind</option>
                                                <option value="Found a better price">Found a better price</option>
                                                <option value="Delivery is too slow">Delivery is too slow</option>
                                                <option value="Ordered by mistake">Ordered by mistake</option>
                                                <option value="Other">Other</option>
                                            </>
                                        ) : (
                                            <>
                                                <option value="Defective item">Defective item</option>
                                                <option value="Wrong item received">Wrong item received</option>
                                                <option value="Quality not as expected">Quality not as expected</option>
                                                <option value="Damaged during transit">Damaged during transit</option>
                                                <option value="Other">Other</option>
                                            </>
                                        )}
                                    </select>
                                </div>
                                {(actionType === "RETURN" || actionType === "REPLACE") && (
                                    <div className="order-modal-field">
                                        <label>Additional details (optional)</label>
                                        <textarea
                                            placeholder="Additional details (optional)"
                                            rows={3}
                                            value={details}
                                            onChange={(e) => setDetails(e.target.value)}
                                            disabled={actionLoading}
                                        />
                                    </div>
                                )}
                            </div>
                            <div className="order-modal-footer">
                                <button
                                    type="button"
                                    className="order-modal-cancel-btn"
                                    onClick={() => setShowModal(false)}
                                    disabled={actionLoading}
                                >
                                    Close
                                </button>
                                <button
                                    type="button"
                                    className={`order-modal-submit-btn ${actionType === "CANCEL" ? "danger" : "primary"}`}
                                    onClick={handleActionSubmit}
                                    disabled={actionLoading || !reason}
                                >
                                    {actionLoading && <span className="order-modal-spinner" />}
                                    {actionType === "CANCEL"
                                        ? "Confirm Cancel"
                                        : actionType === "RETURN"
                                            ? "Submit Return"
                                            : "Submit Replacement"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Customer Support Chat Panel */}
            <ChatPanel
                open={chatOpen}
                onClose={() => setChatOpen(false)}
                order={currentOrder as any}
            />
        </>
    );
};

export default OrderDetailsPage;
